import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Home from './Home';
import React, { Suspense, useState, useEffect } from "react";
import Header from "./Header";
import PageViewTracker from "./PageViewTracker";
import NativeNavigation from "./nativeNavigation";
import lazyWithReload from "./lazyWithReload";
import RouteErrorBoundary from "./RouteErrorBoundary";
import SignupAs from "./signup/SignupAs";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { initStatusBar } from "./statusBar";
import { Helmet } from 'react-helmet-async';
import { hideSplash, startSplashFallback } from "./splashScreen";
import Loader from "./components/Loader";

// Home (the `/` and `/nexxup` homepage) and Header are the only
// routes/components every visitor loads, so they stay eagerly bundled for
// first paint. Everything else - the leaderboard (and the chart.js its
// sparklines pull in), and every admin-only page, which the overwhelming
// majority of visitors never navigate to - is lazy-loaded so its code (and
// anything heavy it pulls in, like maplibre-gl for the heatmap) only
// downloads when that specific route is actually visited, instead of
// shipping in the initial bundle for every single page view.
const ArtistsTable = lazyWithReload(() => import('./artists_table'));
const HeatMap = lazyWithReload(() => import('./heatmap'));
const ArtistPage = lazyWithReload(() => import('./ArtistPage'));
const ArtistLinkHub = lazyWithReload(() => import('./ArtistLinkHub'));
const CompareArtists = lazyWithReload(() => import('./CompareArtists'));
const Login = lazyWithReload(() => import('./Login'));
const GenreDiscovery = lazyWithReload(() => import('./GenreDiscovery'));
const ScoutScore = lazyWithReload(() => import('./ScoutScore'));
const Signup = lazyWithReload(() => import('./signup/Signup'));
const FanAccount = lazyWithReload(() => import('./FanAccount'));
const FanProfile = lazyWithReload(() => import('./FanProfile'));
const FanLeaderboard = lazyWithReload(() => import('./FanLeaderboard'));
const FanEditProfile = lazyWithReload(() => import('./FanEditProfile'));
const ArtistAccount = lazyWithReload(() => import('./ArtistAccount'));
const Help = lazyWithReload(() => import('./Help'));
const CopyrightPolicy = lazyWithReload(() => import('./CopyrightPolicy'));
const PrivacyPolicy = lazyWithReload(() => import('./PrivacyPolicy'));
const TermsOfService = lazyWithReload(() => import('./TermsOfService'));
const AdminSampleReview = lazyWithReload(() => import('./AdminSampleReview'));
const AdminClaimRequests = lazyWithReload(() => import('./AdminClaimRequests'));
const AdminUserManagement = lazyWithReload(() => import('./AdminUserManagement'));
const AdminArtistManagement = lazyWithReload(() => import('./AdminArtistManagement'));
const AdminBadgeSettings = lazyWithReload(() => import('./AdminBadgeSettings'));
const AdminEconomy = lazyWithReload(() => import('./AdminEconomy'));
const AboutNexxup = lazyWithReload(() => import('./AboutNexxup'));
const ArtistApplication = lazyWithReload(() => import('./ArtistApplication'));
const AdminSubmissionReview = lazyWithReload(() => import('./AdminSubmissionReview'));
const AdminGenres = lazyWithReload(() => import('./adminGenres/AdminGenres'));
const AdminInit = lazyWithReload(() => import('./AdminInit'));
const AdminPortal = lazyWithReload(() => import('./AdminPortal'));
const AdminPredictionMarketConcept = lazyWithReload(() => import('./AdminPredictionMarketConcept'));
const AdminCeilingAnchors = lazyWithReload(() => import('./AdminCeilingAnchors'));
const AdminBotReview = lazyWithReload(() => import('./AdminBotReview'));
const AdminShowsReview = lazyWithReload(() => import('./AdminShowsReview'));
const AdminActTypes = lazyWithReload(() => import('./AdminActTypes'));
const ArtistEditProfile = lazyWithReload(() => import('./ArtistEditProfile'));
const LinkEditor = lazyWithReload(() => import('./linkEditor/LinkEditor'));
const SpotifyCallback = lazyWithReload(() => import('./SpotifyCallback'));
const Shop = lazyWithReload(() => import('./Shop'));
const HallOfFame = lazyWithReload(() => import('./HallOfFame'));
const SlimInducteeProfile = lazyWithReload(() => import('./SlimInducteeProfile'));

// Clears a route error when the user navigates elsewhere (see
// RouteErrorBoundary) - needs useLocation, so it has to sit inside <Router>.
function RouteErrorReset({ children }) {
  const { pathname } = useLocation();
  return <RouteErrorBoundary resetKey={pathname}>{children}</RouteErrorBoundary>;
}

// What shows while a route's code loads (every page but the homepage is
// split out); the same loader every page uses.
const RouteFallback = () => <Loader />;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => { initStatusBar(); }, []);
  useEffect(() => { startSplashFallback(); }, []);

  // The profile is only read at sign-in, so the header hears about a new
  // photo from ProfilePictureUpload rather than waiting for a reload.
  useEffect(() => {
    const onPhoto = (e) => setUserProfile((prev) => (prev ? { ...prev, photoURL: e.detail } : prev));
    window.addEventListener("nx-profile-photo", onPhoto);
    return () => window.removeEventListener("nx-profile-photo", onPhoto);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      } else {
        setUserProfile(null);
      }
      loading && setLoading(false);
      hideSplash();
    });

    return () => unsubscribe();
  }, [loading]);

  const getAccountPath = () => {
    if (!userProfile) return "/fan-account";
    // Admin perms are additive, not a replacement account type - an admin
    // still has their own fan/artist profile, reached the same way as
    // everyone else. Admins get to /admin via the dedicated header button
    // instead, so this link never gets hijacked away from their profile.
    return userProfile.accountType === "artist" ? "/artist-account" : "/fan-account";
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Underground Music Tracker | Nexxup</title>
      </Helmet>
      <Router>
        <div className="App">
          <PageViewTracker user={user} />
          <NativeNavigation />
          <Header
            user={user}
            userProfile={userProfile}
            getAccountPath={getAccountPath}
          />
          <RouteErrorReset>
            <Suspense fallback={<RouteFallback />}>
              <main>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/nexxup" element={<Home />} />
                  <Route path="/leaderboard" element={<ArtistsTable />} />
                  <Route path="/heatmap" element={<HeatMap />} />
                  <Route path="/discover" element={<GenreDiscovery />} />
                  <Route path="/discover/:umbrella" element={<GenreDiscovery />} />
                  <Route path="/discover/:umbrella/:genre" element={<GenreDiscovery />} />
                  <Route path="/scout-score" element={<ScoutScore />} />
                  <Route path="/artist/:artistName" element={<ArtistPage />} />
                  <Route path="/go/:artistKey" element={<ArtistLinkHub />} />
                  <Route path="/compare" element={<CompareArtists />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/callback" element={<SpotifyCallback />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/fan-signup" element={<SignupAs as="fan" />} />
                  <Route path="/artist-signup" element={<SignupAs as="artist" />} />
                  <Route path="/fan-account" element={<FanAccount />} />
                  <Route path="/fan/:uid" element={<FanProfile />} />
                  <Route path="/fan-leaderboard" element={<FanLeaderboard />} />
                  <Route path="/fan-account/edit" element={<FanEditProfile />} />
                  <Route path="/fan-account/edit/:folder" element={<FanEditProfile />} />
                  <Route path="/fan-settings" element={<Navigate to="/fan-account/edit" replace />} />
                  <Route path="/artist-account" element={<ArtistAccount />} />
                  <Route path="/artist-account/edit" element={<ArtistEditProfile />} />
                  <Route path="/artist-account/edit/:folder" element={<ArtistEditProfile />} />
                  <Route path="/artist-account/link" element={<LinkEditor />} />
                  {/* Old single settings page - now split into Edit Profile folders. */}
                  <Route path="/artist-settings" element={<Navigate to="/artist-account/edit" replace />} />
                  <Route path="/artist-application" element={<ArtistApplication />} />
                  <Route path="/admin" element={<AdminPortal />} />
                  <Route path="/admin/prediction-market" element={<AdminPredictionMarketConcept />} />
                  <Route path="/admin/submissions" element={<AdminSubmissionReview />} />
                  {/* Old Artist Approvals + Signup Review pages, consolidated into Submission Review. */}
                  <Route path="/admin/artist-approval" element={<Navigate to="/admin/submissions" replace />} />
                  <Route path="/admin/genres" element={<AdminGenres />} />
                  {/* Old Genre Cleanup page, now the Genres page's Votes tab. */}
                  <Route path="/admin/genre-cleanup" element={<Navigate to="/admin/genres?tab=votes" replace />} />
                  <Route path="/admin/signup-review" element={<Navigate to="/admin/submissions" replace />} />
                  <Route path="/admin/artists" element={<AdminArtistManagement />} />
                  {/* Former separate screens, now consolidated into Artist Management. */}
                  <Route path="/admin/soundcharts-linking" element={<Navigate to="/admin/artists" replace />} />
                  <Route path="/admin/soundcharts-review" element={<Navigate to="/admin/artists" replace />} />
                  <Route path="/admin/ceiling-anchors" element={<AdminCeilingAnchors />} />
                  <Route path="/admin/bot-review" element={<AdminBotReview />} />
                  <Route path="/admin/shows" element={<AdminShowsReview />} />
                  <Route path="/admin/act-types" element={<AdminActTypes />} />
                  <Route path="/admin/init" element={<AdminInit />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/about" element={<AboutNexxup />} />
                  <Route path="/copyright" element={<CopyrightPolicy />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/admin/samples" element={<AdminSampleReview />} />
                  <Route path="/admin/claims" element={<AdminClaimRequests />} />
                  <Route path="/admin/cleanup" element={<Navigate to="/admin/artists" replace />} />
                  <Route path="/admin/users" element={<AdminUserManagement />} />
                  <Route path="/admin/data-mismatches" element={<Navigate to="/admin/artists" replace />} />
                  <Route path="/admin/badge-settings" element={<AdminBadgeSettings />} />
                  <Route path="/admin/economy" element={<AdminEconomy />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/hall-of-fame" element={<HallOfFame />} />
                  <Route path="/hall-of-fame/:artistId" element={<SlimInducteeProfile />} />
                </Routes>
              </main>
            </Suspense>
          </RouteErrorReset>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;